import { useEffect, useRef, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { CAMERA_STATUS_COLOR, type ICamera, type CameraGridSize } from '@interfaces/ICameras';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { StatusDot } from '@components/ui/StatusDot/StatusDot';
import { AlertIcon, MaximizeIcon } from '@utils/icons';
import { CameraVideo } from '../CameraVideo/CameraVideo';
import styles from './CameraFeed.module.css';

/** Outlined N×N grid for the layout toggle (2 → 2×2, 3 → 3×3, …):
    a rounded square split by (n−1) horizontal and vertical dividers. */
function GridIcon({ n, size = 16 }: { n: number; size?: number }) {
  const start = 3;
  const span = 18;
  const lines = [];
  for (let i = 1; i < n; i++) {
    const p = start + (span * i) / n;
    lines.push(<line key={`v${i}`} x1={p} y1={start} x2={p} y2={start + span} />);
    lines.push(<line key={`h${i}`} x1={start} y1={p} x2={start + span} y2={p} />);
  }
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <rect x="3" y="3" width="18" height="18" rx="2" />
      {lines}
    </svg>
  );
}

interface ICameraFeedProps {
  cameras: ICamera[];
  gridSize: CameraGridSize;
  onGridSizeChange: (size: CameraGridSize) => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onExpand: (id: string) => void;
}

const GRID_SIZES: CameraGridSize[] = [2, 3, 4];

export function CameraFeed({
  cameras,
  gridSize,
  onGridSizeChange,
  selectedId,
  onSelect,
  onExpand,
}: ICameraFeedProps) {
  const { t } = useTranslation();
  const gridRef = useRef<HTMLDivElement>(null);

  // Scroll the selected camera's tile into view — e.g. when it's picked
  // from the list and lives beyond the current N×N viewport.
  useEffect(() => {
    if (!selectedId || !gridRef.current) return;
    const tile = gridRef.current.querySelector<HTMLElement>(
      `[data-camera-id="${selectedId}"]`,
    );
    tile?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedId]);

  return (
    <section className={styles.panel}>
      <header className={styles.header}>
        <h2 className={styles.title}>{t('cameras.feed.title')}</h2>
        <div className={styles.gridToggle} role="group" aria-label={t('cameras.feed.layout')}>
          {GRID_SIZES.map((size) => (
            <button
              key={size}
              type="button"
              className={styles.gridButton}
              data-active={gridSize === size}
              onClick={() => onGridSizeChange(size)}
              aria-pressed={gridSize === size}
            >
              <GridIcon n={size} />
              <span>{size}×{size}</span>
            </button>
          ))}
        </div>
      </header>

      <ScrollArea
        className={styles.gridScroll}
        viewportClassName={styles.grid}
        viewportRef={gridRef}
        viewportProps={{ style: { '--cols': gridSize } as CSSProperties }}
      >
        {cameras.length === 0 && (
          <p className={styles.empty}>{t('cameras.feed.empty')}</p>
        )}
        {cameras.map((camera) => {
          const isSelected = camera.id === selectedId;
          return (
            <div
              key={camera.id}
              className={styles.tile}
              data-camera-id={camera.id}
              data-status={camera.status}
              data-selected={isSelected}
            >
              <button
                type="button"
                className={styles.selectBtn}
                aria-pressed={isSelected}
                aria-label={camera.name}
                onClick={() => onSelect(isSelected ? null : camera.id)}
              >
                <span className={styles.badge} data-status={camera.status}>
                  <StatusDot color={CAMERA_STATUS_COLOR[camera.status]} size={6} />
                  {t(`cameras.status.${camera.status}`)}
                </span>

                <div className={styles.viewport} data-status={camera.status}>
                  <CameraVideo videoSrc={camera.videoSrc} status={camera.status} iconSize={28} />
                  {camera.status === 'alert' && camera.alertLabel && (
                    <span className={styles.alertPill}>
                      <AlertIcon size={12} />
                      {camera.alertLabel}
                    </span>
                  )}
                </div>

                <div className={styles.caption}>
                  <span className={styles.tileName}>{camera.name}</span>
                  <span className={styles.tileMeta}>{camera.id} · {camera.floor}</span>
                </div>
              </button>

              <button
                type="button"
                className={styles.expand}
                aria-label={t('cameras.feed.expand')}
                onClick={() => onExpand(camera.id)}
              >
                <MaximizeIcon size={14} />
              </button>
            </div>
          );
        })}
      </ScrollArea>
    </section>
  );
}
